import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { detectExtensionSupport, NO_MOBILE } from '@/utils/extensionSupport';
import { clipperSentence, CLIPPER_VERSION, CLIPPER_ZIP } from './clipperSentence';

export { clipperSentence, CLIPPER_VERSION, CLIPPER_ZIP };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DP30 — the clipper row says only what the app can MEASURE.
 *
 * WHAT IT CANNOT DO: a web page cannot ask Chrome what is installed. The
 * v3.0.0 build does not stamp the page either, so there is no installed
 * signal at all. DP29 said "not installed on this device", which is a
 * confident wrong answer for anybody who has just installed it.
 *
 * WHAT IT CAN DO: read when a clip last arrived for this firm. That is a
 * measured fact, and it is worded as one. It is a HEURISTIC, not detection:
 *   - the clip could have come from a partner's computer, so a fresh machine
 *     with nothing installed can read "last clip 3 days ago";
 *   - a real install that has not clipped anything yet reads "no clips yet",
 *     so the row also says that clipping something will confirm it.
 * It cannot tell "installed but not signed in" from "not installed", and it
 * does not pretend to: neither sentence claims an installation state.
 *
 * The marker branch below is the one honest detection path, kept ready: when
 * a future build sets `documentElement.dataset.keymadeClipper`, the row can
 * state the installed version as fact.
 */
export function readClipperMarker() {
  if (typeof document === 'undefined') return null;
  return document.documentElement?.dataset?.keymadeClipper || null;
}

export function useClipperStatus() {
  const [marker, setMarker] = useState(() => readClipperMarker());
  const [lastClip, setLastClip] = useState({ loading: true, days: null, at: null });

  useEffect(() => {
    const t = setTimeout(() => setMarker(readClipperMarker()), 1200);
    let alive = true;
    axios.get(`${API}/clipper/last-clip`)
      .then(({ data }) => {
        if (alive) setLastClip({ loading: false, days: data.days_ago, at: data.last_clip_at });
      })
      .catch(() => { if (alive) setLastClip({ loading: false, days: null, at: null }); });
    return () => { alive = false; clearTimeout(t); };
  }, []);

  const support = detectExtensionSupport();
  return { marker, lastClip, support };
}

function ago(days) {
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

export function downloadClipper() {
  const a = document.createElement('a');
  a.href = CLIPPER_ZIP;
  a.download = 'keymade-clipper-v3.zip';
  a.click();
  toast.success('Downloading. Unzip it, then chrome://extensions → Developer mode → Load unpacked');
}

export const ClipperState = ({ compact = false, darkMode = false, testId = 'clipper-state' }) => {
  const status = useClipperStatus();
  const { headline, action, hint, share } = clipperSentence(status);

  return (
    <div
      className={`${compact ? 'text-xs' : 'text-sm'} ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
      data-testid={testId}
      data-clipper-state={share ? 'cannot-install' : (status.marker ? 'marker' : (status.lastClip.days !== null ? 'clips-seen' : 'no-clips'))}
    >
      <div className="flex items-center gap-2">
        <span className="min-w-0" data-testid={`${testId}-headline`}>{headline}</span>
        {share ? (
          <span className="text-xs opacity-70" data-testid={`${testId}-share`}>
            Capture with Share to KEY-MADE instead
          </span>
        ) : action ? (
          <button type="button" onClick={downloadClipper} className="kmp-btn shrink-0"
                  data-testid={`${testId}-action`}>
            {action}
          </button>
        ) : null}
      </div>
      {hint ? (
        <p className="text-xs opacity-70" data-testid={`${testId}-hint`}>{hint}</p>
      ) : null}
    </div>
  );
};

/**
 * DP30 — where the clipper's absence is why the page looks empty. One
 * dismissible line, once per device, never a popup, never repeated.
 */
export const ClipperAbsenceNotice = ({ where, darkMode = false }) => {
  const key = `keyMadeClipperNoticeDismissed:${where}`;
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(key));
  const status = useClipperStatus();

  if (dismissed) return null;
  if (status.support.state === NO_MOBILE) return null;
  if (status.marker || status.lastClip.loading || status.lastClip.days !== null) return null;

  return (
    <div
      className="kmp-bar kmd-sheet"
      style={{ padding: 'var(--km-s2) var(--km-s3)', marginBottom: 'var(--km-s2)' }}
      data-testid={`clipper-absence-${where}`}
    >
      <p className="text-sm flex-1">
        This is where clipped products arrive. No clips from this device yet — the Web Clipper
        may not be installed here. If it is, clipping something will confirm it.
      </p>
      <ClipperState compact darkMode={darkMode} testId={`clipper-absence-state-${where}`} />
      <button
        type="button"
        className="kmp-btn shrink-0"
        onClick={() => { localStorage.setItem(key, '1'); setDismissed(true); }}
        data-testid={`clipper-absence-dismiss-${where}`}
      >
        Dismiss
      </button>
    </div>
  );
};

export default ClipperState;
